import React, {Component, Fragment} from 'react'
import IntlMessages from 'util/IntlMessages'
import {Row, Col, Card, CardBody, Button, Form, Label} from 'reactstrap'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import moment from 'moment'
import autobind from 'auto-bind'
import Switch from 'rc-switch'
import {CSVLink, CSVDownload} from 'react-csv'
import PropTypes from 'prop-types'
import CustomSelectInput from 'components/CustomSelectInput'

import {ThemeColors} from 'util/ThemeColors'
import * as API from 'SDK/api'
import {getReportAssets} from 'SDK/api/common'

import ReportChart from './chart'
import ExportData from './export'

const colors = ThemeColors()

const TIME_UNIT_OPTIONS = [
    {
        label: 'Total Time',
        value: 'total',
        key: 0,
    },
    {
        label: 'Hours',
        value: 'hours',
        key: 1,
    },
    {
        label: 'Days',
        value: 'days',
        key: 2,
    },
    {
        label: 'Weeks',
        value: 'weeks',
        key: 3,
    },
    {
        label: 'Months',
        value: 'months',
        key: 4,
    },
    {
        label: 'Quarters',
        value: 'quarters',
        key: 5,
    },
    {
        label: 'Years',
        value: 'years',
        key: 6,
    },
]

const getRandomColor = () => {
    let letters = '0123456789ABCDEF'.split(''),
        color = '#'
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)]
    }
    return color
}

export default class UtilizationReport extends Component {
    propComponents = [
        {
            prop: 'name',
            component: 'GenericWidgetName',
        },
        {
            prop: 'deviceIds',
            component: 'MultiAssetPicker',
        },
        {
            prop: 'useERPAssetNames',
            component: 'UseERPAssetNames',
        },
        {
            prop: 'offsetOvernightShifts',
            component: 'OffsetOvernightShifts',
        },
    ]
    showBorder = false
    id = 'UtilizationReport'
    requiredOutputs = ['In-Cycle']
    static propTypes = {
        name: PropTypes.string,
        deviceIds: PropTypes.array,
        useERPAssetNames: PropTypes.bool,
        offsetOvernightShifts: PropTypes.bool,
    }
    constructor(props) {
        super(props)
        autobind(this)

        this.state = {
            ready: false,
            loading: false,
            shiftRecords: [],
            assets: [],
            selectedAssets: [],
            data: [],
            start: moment().startOf('day'),
            end: moment().endOf('day'),
            timeUnit: TIME_UNIT_OPTIONS[0],
            byShift: false,
            hours: false,
            shifts: [],
            type: '',
            csvData: [],
        }
    }
    handleStartChange(date) {
        this.setState({
            start: date,
        })
    }
    handleEndChange(date) {
        this.setState({
            end: date,
        })
    }
    handleTimeUnitChange(e) {
        this.setState({
            timeUnit: TIME_UNIT_OPTIONS.find((x) => e.label === x.label),
        })
    }
    handleAssetChange(e) {
        this.setState({
            selectedAssets: e,
        })
    }
    async getAssets() {
        let assets = await getReportAssets()
        if (!assets) return alert('Could not fetch factory assets.')

        const labelKey = this.props.useERPAssetNames ? 'erpAssetName' : 'name'
        if (
            labelKey === 'erpAssetName' &&
            assets.some((a) => a[labelKey] === undefined || a[labelKey] === '')
        ) {
            return alert(
                'Cannot use ERP Asset Names. Not all assets are assigned an ERP Asset Name.'
            )
        }

        assets = assets.map((a) => {
            a.name = a[labelKey]
            return a
        })

        this.setState({
            assets: assets,
            selectedAssets: assets.map((a, i) => {
                return {label: a.name, value: a.deviceId, key: i}
            }),
        })
    }

    async getShifts() {
        const shifts = await API.get('shifts')
        if (!shifts) return alert('Could not fetch factory shifts.')

        this.setState({
            shifts: [...new Set(shifts.map((x) => x.name))],
        })
    }

    getDeviceData(deviceId, shift) {
        let timeUnit =
            this.state.timeUnit.value === 'total'
                ? 'years'
                : this.state.timeUnit.value
        let obj = {
            timeStart: moment(this.state.start).toISOString(),
            timeEnd: moment(this.state.end).subtract(0, 'hours').toISOString(),
            deviceId: deviceId,
            state: 'In-Cycle',
            unit: timeUnit,
        }
        if (shift) {
            if (this.props.offsetOvernightShifts) {
                const shiftObject = this.state.shiftRecords.find(
                    (s) => s.name === shift && s.assets.includes(deviceId)
                )
                if (
                    shiftObject &&
                    shiftObject.timeStart.hour > shiftObject.timeEnd.hour
                ) {
                    obj.timeStart = moment(obj.timeStart)
                        .add(-1, 'days')
                        .toISOString()
                    obj.timeEnd = moment(obj.timeEnd)
                        .add(-1, 'days')
                        .toISOString()
                }
            }
            obj.shift = shift
        }
        return API.post('historical', obj)
    }

    async generateTotal() {
        let promises = []

        for (let i in this.state.selectedAssets) {
            promises.push(
                this.getDeviceData(this.state.selectedAssets[i].value)
            )
        }

        const data = await Promise.all(promises)

        const capacity = moment(this.state.end).diff(
            moment(this.state.start),
            'minutes'
        )
        let chartdata = {
            labels: [],
            datasets: [
                {
                    label: 'Utilization',
                    data: [],
                    backgroundColor: colors.themeColor1,
                },
            ],
        }
        for (let i in data) {
            let deviceName = this.state.assets.find(
                (x) => x.deviceId === data[i].query.deviceId
            ).name
            chartdata.labels.push(deviceName)
            if (this.state.hours) {
                chartdata.datasets[0].data.push(
                    Math.round((data[i].duration[0] / 60) * 100) / 100
                )
            } else {
                chartdata.datasets[0].data.push(
                    parseInt((data[i].duration[0] / capacity) * 100)
                )
            }
        }
        this.setState({
            data: {chartdata: chartdata, hours: this.state.hours},
            ready: true,
            loading: false,
        })
    }
    async generateTotalByShift() {
        await this.getShifts()
        let self = this
        let chartdata = {
            labels: this.state.selectedAssets.map((x) => x.label),
            datasets: this.state.shifts.map((x) => {
                return {
                    label: x,
                    data: [],
                    backgroundColor: getRandomColor(),
                }
            }),
        }
        let promises = []
        for (let i in this.state.selectedAssets) {
            for (let j in this.state.shifts) {
                if (
                    this.state.shiftRecords.find(
                        (s) =>
                            s.name === this.state.shifts[j] &&
                            s.assets.find(
                                (asset) =>
                                    asset === this.state.selectedAssets[i].value
                            )
                    )
                ) {
                    promises.push(
                        this.getDeviceData(
                            this.state.selectedAssets[i].value,
                            this.state.shifts[j]
                        )
                    )
                }
            }
        }
        Promise.all(promises)
            .then((data) => {
                for (let i in data) {
                    let d = data[i]
                    let datasetIndex = chartdata.labels.findIndex(
                        (x) =>
                            x ===
                            this.state.assets.find(
                                (y) => y.deviceId === d.query.deviceId
                            ).name
                    )
                    let chartdataIndex = chartdata.datasets.findIndex(
                        (x) => x.label === d.query.shift
                    )
                    chartdata.datasets[chartdataIndex].data[datasetIndex] = self
                        .state.hours
                        ? d.duration.map(
                              (x) => Math.round((x / 60) * 100) / 100
                          )[0]
                        : d.durationPercentage.map((x) => x * 100)[0]
                }
                self.setState({
                    data: {chartdata: chartdata, hours: self.state.hours},
                    ready: true,
                    loading: false,
                })
            })
            .catch((error) => {
                console.log(error)
            })
    }
    generate() {
        // validation
        if (this.state.selectedAssets.length === 0)
            return alert('At least one asset must be selected.')

        this.setState({loading: true})
        if (this.state.timeUnit.value === 'total' && !this.state.byShift) {
            this.generateTotal()
            this.setState({type: 'total'})
        } else if (
            this.state.timeUnit.value === 'total' &&
            this.state.byShift
        ) {
            this.generateTotalByShift()
            this.setState({type: 'totalByShift'})
        } else if (
            this.state.timeUnit.value !== 'total' &&
            !this.state.byShift
        ) {
            this.generateTimeSeries()
            this.setState({type: 'timeSeries'})
        } else if (
            this.state.timeUnit.value !== 'total' &&
            this.state.byShift
        ) {
            if (this.state.timeUnit.value === 'hours') {
                this.setState({loading: false})
                return alert(
                    'Time Unit must be greater than hours when generating shift reports.'
                )
            }
            this.generateTimeSeriesByShift()
            this.setState({type: 'timeSeriesByShift'})
        }
        let csvData = this.exportData()
        this.setState({csvData})
    }
    async generateTimeSeries() {
        let self = this
        let chartdata = {
            labels: [],
            datasets: [],
        }
        Promise.all(
            this.state.selectedAssets.map((x) => this.getDeviceData(x.value))
        )
            .then((data) => {
                if (self.state.timeUnit.value === 'hours') {
                    chartdata.labels = data[0].dates.map((x) =>
                        moment(x).format('YYYY-MM-DD h a')
                    )
                } else if (self.state.timeUnit.value !== 'total') {
                    chartdata.labels = data[0].dates.map((x) =>
                        moment(x).format('YYYY-MM-DD')
                    )
                }
                chartdata.datasets = data.map((d) => {
                    return {
                        label: self.state.assets.find(
                            (x) => x.deviceId === d.query.deviceId
                        ).name,
                        data: self.state.hours
                            ? d.duration.map(
                                  (x) => Math.round((x / 60) * 100) / 100
                              )
                            : d.durationPercentage.map((x) => x * 100),
                        backgroundColor: getRandomColor(),
                    }
                })
                self.setState({
                    data: {chartdata: chartdata, hours: self.state.hours},
                    ready: true,
                    loading: false,
                })
                //console.log(chartdata)
            })
            .catch((error) => {
                console.log(error)
            })
    }
    async generateTimeSeriesByShift() {
        await this.getShifts()
        let self = this
        let chartdatas = []

        const colors = this.state.selectedAssets.map((x) => getRandomColor())

        for (let i in this.state.shifts) {
            chartdatas[i] = {
                shift: this.state.shifts[i],
                labels: [],
                datasets: this.state.selectedAssets.map((x, i) => {
                    return {
                        label: x.label,
                        data: [],
                        backgroundColor: colors[i],
                    }
                }),
            }
        }
        let promises = []
        for (let i in this.state.selectedAssets) {
            for (let j in this.state.shifts) {
                if (
                    this.state.shiftRecords.find(
                        (s) =>
                            s.name === this.state.shifts[j] &&
                            s.assets.find(
                                (asset) =>
                                    asset === this.state.selectedAssets[i].value
                            )
                    )
                ) {
                    promises.push(
                        this.getDeviceData(
                            this.state.selectedAssets[i].value,
                            this.state.shifts[j]
                        )
                    )
                }
            }
        }
        Promise.all(promises)
            .then((data) => {
                if (this.props.offsetOvernightShifts) {
                    for (let dataset of data) {
                        const shiftRecord = this.state.shiftRecords.find(
                            (s) =>
                                s.name === dataset.query.shift &&
                                s.assets.includes(dataset.query.deviceId)
                        )
                        if (
                            shiftRecord &&
                            shiftRecord.timeStart.hour >
                                shiftRecord.timeEnd.hour
                        ) {
                            dataset.dates = dataset.dates.map((d) => {
                                return moment(d).add(1, 'days').toISOString()
                            })
                        }
                    }
                }
                for (let i in chartdatas) {
                    if (self.state.timeUnit.value === 'hours') {
                        chartdatas[i].labels = data[0].dates.map((x) =>
                            moment(x).format('YYYY-MM-DD h a')
                        )
                    } else if (self.state.timeUnit.value !== 'total') {
                        chartdatas[i].labels = data[0].dates.map((x) =>
                            moment(x).format('YYYY-MM-DD')
                        )
                    }
                }
                for (let i in data) {
                    let d = data[i] //.data;
                    // chartIndex by shift
                    let chartdataIndex = chartdatas.findIndex(
                        (x) => x.shift === d.query.shift
                    )
                    // datasetIndex by device
                    let datasetIndex = chartdatas[
                        chartdataIndex
                    ].datasets.findIndex(
                        (x) =>
                            x.label ===
                            this.state.assets.find(
                                (y) => y.deviceId === d.query.deviceId
                            ).name
                    )
                    chartdatas[chartdataIndex].datasets[datasetIndex].data =
                        self.state.hours
                            ? d.duration.map(
                                  (x) => Math.round((x / 60) * 100) / 100
                              )
                            : d.durationPercentage.map((x) => x * 100)
                }
                self.setState({
                    data: chartdatas,
                    ready: true,
                    loading: false,
                })
            })
            .catch((error) => {
                console.log(error)
            })
    }
    exportData() {
        const type = this.state.type,
            data = this.state.data,
            unit = this.state.hours ? 'Hours' : '%'

        let exportObject = []

        if (type === 'total') {
            let header = ['Time']
            for (let i in this.state.selectedAssets) {
                header.push(this.state.selectedAssets[i].label + ' ' + unit)
            }
            exportObject.push(header)
            let onlyRow = ['Total']
            onlyRow = onlyRow.concat(data.chartdata.datasets[0].data)
            exportObject.push(onlyRow)
        } else if (type === 'totalByShift') {
            let header = ['Time'],
                onlyRow = ['Total']
            for (let i in this.state.selectedAssets) {
                for (let j in this.state.shifts) {
                    header.push(
                        this.state.selectedAssets[i].label +
                            ' - ' +
                            this.state.shifts[j] +
                            ' ' +
                            unit
                    )
                    onlyRow.push(data.chartdata.datasets[j].data[i])
                }
            }
            exportObject.push(header)
            exportObject.push(onlyRow)
        } else if (type === 'timeSeries') {
            let header = ['Time']
            for (let i in this.state.selectedAssets) {
                header.push(this.state.selectedAssets[i].label + ' ' + unit)
            }
            exportObject.push(header)
            for (let i in data.chartdata.labels) {
                let row = [data.chartdata.labels[i]]
                for (let j in this.state.selectedAssets) {
                    row.push(data.chartdata.datasets[j].data[i])
                }
                exportObject.push(row)
            }
        } else if (type === 'timeSeriesByShift') {
            let header = ['Time']
            for (let i in this.state.selectedAssets) {
                for (let j in this.state.shifts) {
                    header.push(
                        this.state.selectedAssets[i].label +
                            ' - ' +
                            this.state.shifts[j] +
                            ' ' +
                            unit
                    )
                }
            }
            exportObject.push(header)
            for (let i in data[0].labels) {
                let row = [data[0].labels[i]]
                for (let j in data) {
                    for (let k in data[j].datasets) {
                        row.push(data[j].datasets[k].data[i])
                    }
                }

                exportObject.push(row)
            }
        }
        return exportObject
    }
    generateCharts() {
        const exportSection = (
            <Row>
                <Col xs="12" style={{textAlign: 'right'}} className="mb-4">
                    <Button
                        outline
                        size="xs"
                        color="primary"
                        onClick={this.exportData}>
                        <i className="iconsmind-Printer" /> Export Data
                    </Button>
                </Col>
            </Row>
        )

        if (this.state.ready && !this.state.loading) {
            let charts = [],
                max = 0
            if (this.state.data.constructor === Array) {
                for (let i in this.state.data) {
                    for (let j in this.state.data[i].datasets) {
                        for (let k in this.state.data[i].datasets[j].data) {
                            if (this.state.data[i].datasets[j].data[k] > max) {
                                max = this.state.data[i].datasets[j].data[k]
                            }
                        }
                    }
                    let data = {
                        chartdata: this.state.data[i],
                        hours: this.state.hours,
                        max: max,
                    }
                    charts.push(data)
                }
            } else {
                charts.push(this.state.data)
            }
            return charts.map((c, idx) => <ReportChart key={idx} data={c} />)
        } else {
            return <p>Loading...</p>
        }
    }
    async componentWillMount() {
        this.setState({shiftRecords: await API.get('shifts', 2)})
        await this.getAssets()
    }

    render() {
        const readyToExport = this.state.ready && !this.state.loading
        return (
            <Fragment>
                <Row>
                    <Col xs="12" className="mb-4">
                        <Card>
                            <CardBody>
                                <Row>
                                    <Col xs="12" className="mb-4">
                                        <label>Assets:</label>
                                        <Select
                                            components={{
                                                Input: CustomSelectInput,
                                            }}
                                            className="react-select"
                                            classNamePrefix="react-select"
                                            isMulti
                                            name="assets"
                                            value={this.state.selectedAssets}
                                            onChange={this.handleAssetChange}
                                            options={this.state.assets.map(
                                                (a, i) => {
                                                    return {
                                                        label: a.name,
                                                        value: a.deviceId,
                                                        key: i,
                                                    }
                                                }
                                            )}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs="12" sm="2">
                                        <label>From:</label>
                                        <DatePicker
                                            selected={this.state.start}
                                            selectsStart
                                            startDate={this.state.start}
                                            endDate={this.state.end}
                                            onChange={this.handleStartChange}
                                            filterDate={(date) => {
                                                return moment() > date
                                            }}
                                            disabled={this.state.loading}
                                        />
                                    </Col>
                                    <Col xs="12" sm="2">
                                        <label>To:</label>
                                        <DatePicker
                                            selected={this.state.end}
                                            selectsEnd
                                            startDate={this.state.start}
                                            endDate={this.state.end}
                                            onChange={this.handleEndChange}
                                            filterDate={(date) => {
                                                return moment() > date
                                            }}
                                            disabled={this.state.loading}
                                        />
                                    </Col>
                                    <Col xs="12" sm="2">
                                        <label>Time Unit:</label>
                                        <Select
                                            components={{
                                                Input: CustomSelectInput,
                                            }}
                                            className="react-select"
                                            classNamePrefix="react-select"
                                            name="time-unit"
                                            value={this.state.timeUnit}
                                            onChange={this.handleTimeUnitChange}
                                            options={TIME_UNIT_OPTIONS}
                                            isDisabled={this.state.loading}
                                        />
                                    </Col>
                                    <Col xs="12" sm="2">
                                        <label>
                                            By Shift (group by shift):
                                        </label>
                                        <Switch
                                            className="custom-switch custom-switch-primary"
                                            checked={this.state.byShift}
                                            onChange={(byShift) => {
                                                this.setState({byShift})
                                            }}
                                            disabled={this.state.loading}
                                        />
                                    </Col>
                                    <Col xs="12" sm="2">
                                        <label>
                                            Hours (instead of percent):
                                        </label>
                                        <Switch
                                            className="custom-switch custom-switch-primary"
                                            checked={this.state.hours}
                                            onChange={(hours) => {
                                                this.setState({hours})
                                            }}
                                            disabled={this.state.loading}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs="12" style={{textAlign: 'right'}}>
                                        <br />
                                        <Button
                                            color="primary"
                                            onClick={this.generate}
                                            disabled={this.state.loading}>
                                            <i className="iconsmind-Arrow-Refresh" />{' '}
                                            Generate
                                        </Button>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                {readyToExport ? (
                    <ExportData
                        type={this.state.type}
                        selectedAssets={this.state.selectedAssets}
                        data={this.state.data}
                        hours={this.state.hours}
                        shifts={this.state.shifts}
                    />
                ) : null}
                {this.state.ready ? this.generateCharts() : null}
                {this.state.loading ? <div className="loading" /> : null}
            </Fragment>
        )
    }
}
